/**
 * Accessibility Manager
 * WebGLアプリケーション用アクセシビリティ管理システム
 */

import { webglLogger } from '@/lib/utils/logger'

export interface AccessibilityConfig {
  /** 音声読み上げ有効 */
  enableScreenReader: boolean
  /** 高コントラストモード */
  highContrastMode: boolean
  /** 減速アニメーション */
  reducedMotion: boolean
  /** 拡大表示 */
  magnification: number
  /** キーボードナビゲーション */
  keyboardNavigation: boolean
  /** 音響フィードバック */
  audioFeedback: boolean
  /** 触覚フィードバック */
  hapticFeedback: boolean
  /** 色覚支援 */
  colorBlindSupport: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia'
  /** フォーカス表示 */
  showFocusIndicator: boolean
  /** 音量レベル */
  audioVolume: number
  /** キーボードショートカットのスキップリンク */
  skipLinks: boolean
  /** フォーカス内ループ */
  focusLoop: boolean
  /** キーボード操作のみのフォーカス表示 */
  keyboardFocusOnly: boolean
}

export interface AccessibilityDescription {
  /** 要素ID */
  elementId: string
  /** 要素タイプ */
  elementType: 'button' | 'slider' | 'canvas' | 'text' | 'image' | 'control'
  /** 説明テキスト */
  description: string
  /** 値（該当する場合） */
  value?: string | number
  /** 状態 */
  state?: 'active' | 'inactive' | 'disabled' | 'loading'
  /** 役割 */
  role?: string
  /** ラベル */
  label?: string
  /** ヘルプテキスト */
  helpText?: string
}

export interface KeyboardShortcut {
  /** キーの組み合わせ */
  keys: string[]
  /** アクション */
  action: string
  /** 説明 */
  description: string
  /** 有効化条件 */
  condition?: () => boolean
  /** 実行関数 */
  handler: () => void
}

export interface FocusableElement {
  /** 要素ID */
  id: string
  /** 要素参照 */
  element: HTMLElement
  /** フォーカス可能かどうか */
  focusable: boolean
  /** タブインデックス */
  tabIndex: number
  /** 説明 */
  description?: AccessibilityDescription
  /** 親要素ID */
  parentId?: string
  /** 子要素ID配列 */
  childIds?: string[]
}

/**
 * アクセシビリティマネージャー
 */
export class AccessibilityManager {
  private config: AccessibilityConfig
  private descriptions: Map<string, AccessibilityDescription> = new Map()
  private shortcuts: Map<string, KeyboardShortcut> = new Map()
  private focusableElements: Map<string, FocusableElement> = new Map()
  private currentFocus: string | null = null
  private speechSynth: SpeechSynthesis | null = null
  private callbacks: Map<string, (config: AccessibilityConfig) => void> = new Map()

  constructor(config: Partial<AccessibilityConfig> = {}) {
    this.config = {
      enableScreenReader: true,
      highContrastMode: false,
      reducedMotion: false,
      magnification: 1.0,
      keyboardNavigation: true,
      audioFeedback: true,
      hapticFeedback: true,
      colorBlindSupport: 'none',
      showFocusIndicator: true,
      audioVolume: 0.7,
      skipLinks: true,
      focusLoop: true,
      keyboardFocusOnly: false,
      ...config
    }

    // Web Speech API の初期化
    if ('speechSynthesis' in window) {
      this.speechSynth = window.speechSynthesis
    }

    this.initializeDefaultShortcuts()
    this.setupEventListeners()
    this.applyAccessibilityPreferences()

    webglLogger.info('[AccessibilityManager] Initialized')
  }

  /**
   * デフォルトキーボードショートカットを初期化
   */
  private initializeDefaultShortcuts(): void {
    // スキップリンク
    if (this.config.skipLinks) {
      this.addShortcut('skip-to-main', {
        keys: ['Alt', 'S'],
        action: 'Skip to Main Content',
        description: 'メインコンテンツにスキップ',
        handler: () => this.skipToMainContent()
      })

      this.addShortcut('skip-to-navigation', {
        keys: ['Alt', 'N'],
        action: 'Skip to Navigation',
        description: 'ナビゲーションにスキップ',
        handler: () => this.skipToNavigation()
      })
    }

    // 基本ナビゲーション
    this.addShortcut('focus-next', {
      keys: ['Tab'],
      action: 'Focus Next',
      description: '次の要素にフォーカス',
      handler: () => this.focusNext()
    } as KeyboardShortcut);

    this.addShortcut('focus-previous', {
      keys: ['Shift', 'Tab'],
      action: 'Focus Previous',
      description: '前の要素にフォーカス',
      handler: () => this.focusPrevious()
    } as KeyboardShortcut);

    // アクセシビリティ機能
    this.addShortcut('toggle-screen-reader', {
      keys: ['Alt', 'Shift', 'S'],
      action: 'Toggle Screen Reader',
      description: '音声読み上げの切り替え',
      handler: () => this.toggleScreenReader()
    } as KeyboardShortcut);

    this.addShortcut('toggle-high-contrast', {
      keys: ['Alt', 'Shift', 'C'],
      action: 'Toggle High Contrast',
      description: '高コントラストモードの切り替え',
      handler: () => this.toggleHighContrast()
    } as KeyboardShortcut);

    this.addShortcut('toggle-reduced-motion', {
      keys: ['Alt', 'Shift', 'M'],
      action: 'Toggle Reduced Motion',
      description: '減速アニメーションの切り替え',
      handler: () => this.toggleReducedMotion()
    } as KeyboardShortcut);

    // 拡大/縮小
    this.addShortcut('zoom-in', {
      keys: ['Ctrl', '+'],
      action: 'Zoom In',
      description: '拡大',
      handler: () => this.changeMagnification(0.1)
    } as KeyboardShortcut);

    this.addShortcut('zoom-out', {
      keys: ['Ctrl', '-'],
      action: 'Zoom Out',
      description: '縮小',
      handler: () => this.changeMagnification(-0.1)
    } as KeyboardShortcut);

    this.addShortcut('zoom-reset', {
      keys: ['Ctrl', '0'],
      action: 'Reset Zoom',
      description: 'ズームリセット',
      handler: () => this.setMagnification(1.0)
    } as KeyboardShortcut);

    // ヘルプ
    this.addShortcut('show-help', {
      keys: ['F1'],
      action: 'Show Help',
      description: 'ヘルプの表示',
      handler: () => this.showHelp()
    } as KeyboardShortcut)
  }

  /**
   * ヘルプを表示
   */
  private showHelp(): void {
    // ヘルプ表示の実装
    this.announceAction('アクセシビリティヘルプを表示します')
  }

  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    document.addEventListener('keydown', this.handleKeyDown.bind(this))
    document.addEventListener('focus', this.handleFocus.bind(this), true)
    document.addEventListener('blur', this.handleBlur.bind(this), true)

    // キーボード操作検出
    if (this.config.keyboardFocusOnly) {
      document.addEventListener('mousedown', this.handleMouseDown.bind(this), true)
      document.addEventListener('keydown', this.handleKeyboardEvent.bind(this), true)
    }

    // システム設定の変更を監視
    if ('matchMedia' in window) {
      // prefers-reduced-motion
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      reducedMotionQuery.addEventListener('change', (event: MediaQueryListEvent) => {
        if (event.matches) {
          this.config.reducedMotion = true
          this.notifyConfigChange('reducedMotion', true)
        }
      })

      // prefers-contrast
      const highContrastQuery = window.matchMedia('(prefers-contrast: high)')
      highContrastQuery.addEventListener('change', (event: MediaQueryListEvent) => {
        if (event.matches) {
          this.config.highContrastMode = true
          this.notifyConfigChange('highContrastMode', true)
        }
      })
    }
  }

  /**
   * アクセシビリティ設定を適用
   */
  private applyAccessibilityPreferences(): void {
    // システム設定から自動検出
    if ('matchMedia' in window) {
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      if (reducedMotionQuery.matches) {
        this.config.reducedMotion = true
      }

      const highContrastQuery = window.matchMedia('(prefers-contrast: high)')
      if (highContrastQuery.matches) {
        this.config.highContrastMode = true
      }
    }

    // CSS変数で設定を適用
    this.updateCSSProperties()
  }

  /**
   * CSS変数を更新
   */
  private updateCSSProperties(): void {
    const root = document.documentElement

    // 拡大率
    root.style.setProperty('--a11y-scale', this.config.magnification.toString())

    // 高コントラスト
    if (this.config.highContrastMode) {
      root.style.setProperty('--a11y-bg-color', '#000000')
      root.style.setProperty('--a11y-text-color', '#ffffff')
      root.style.setProperty('--a11y-border-color', '#ffffff')
      root.style.setProperty('--a11y-focus-color', '#ffff00')
    } else {
      root.style.removeProperty('--a11y-bg-color')
      root.style.removeProperty('--a11y-text-color')
      root.style.removeProperty('--a11y-border-color')
      root.style.removeProperty('--a11y-focus-color')
    }

    // 減速アニメーション
    if (this.config.reducedMotion) {
      root.style.setProperty('--a11y-animation-duration', '0.01s')
      root.style.setProperty('--a11y-transition-duration', '0.01s')
    } else {
      root.style.removeProperty('--a11y-animation-duration')
      root.style.removeProperty('--a11y-transition-duration')
    }

    // フォーカスインジケーター
    if (this.config.showFocusIndicator) {
      root.style.setProperty('--a11y-focus-outline', '3px solid var(--a11y-focus-color, #007acc)')
    } else {
      root.style.setProperty('--a11y-focus-outline', 'none')
    }
  }

  /**
   * 要素の説明を追加
   */
  public addDescription(description: AccessibilityDescription): void {
    this.descriptions.set(description.elementId, description)

    // 対応するDOM要素があればARIA属性を設定
    const element = document.getElementById(description.elementId)
    if (element) {
      this.updateElementAttributes(element, description)
    }
  }

  /**
   * 要素のARIA属性を更新
   */
  private updateElementAttributes(element: HTMLElement, description: AccessibilityDescription): void {
    if (description.label) {
      element.setAttribute('aria-label', description.label)
    }

    if (description.description) {
      element.setAttribute('aria-describedby', `${description.elementId}-desc`)

      // 説明要素を作成
      let descElement = document.getElementById(`${description.elementId}-desc`)
      if (!descElement) {
        descElement = document.createElement('div')
        descElement.id = `${description.elementId}-desc`
        descElement.className = 'sr-only'
        descElement.textContent = description.description
        element.parentNode?.appendChild(descElement)
      }
    }

    if (description.role) {
      element.setAttribute('role', description.role)
    }

    if (description.state) {
      element.setAttribute('aria-expanded', description.state === 'active' ? 'true' : 'false')
    }

    // 値が存在する場合、aria-valuenowを設定
    if (description.value !== undefined) {
      element.setAttribute('aria-valuenow', description.value.toString())
    }

    // 状態に応じてaria-disabledを設定
    if (description.state === 'disabled') {
      element.setAttribute('aria-disabled', 'true')
    } else if (description.state === 'loading') {
      element.setAttribute('aria-busy', 'true')
    }
  }

  /**
   * 要素の状態を更新
   */
  public updateElementState(elementId: string, state: 'active' | 'inactive' | 'disabled' | 'loading'): void {
    const description = this.descriptions.get(elementId)
    if (description) {
      description.state = state
      const element = document.getElementById(elementId)
      if (element) {
        this.updateElementAttributes(element, description)

        // 状態変更を音声で通知
        if (this.config.enableScreenReader) {
          this.announceAction(`${description.label || elementId} の状態が ${state} に変更されました`)
        }
      }
    }
  }

  /**
   * キーボードショートカットを追加
   */
  public addShortcut(id: string, shortcut: KeyboardShortcut): void {
    this.shortcuts.set(id, shortcut)
  }

  /**
   * フォーカス可能要素を登録
   */
  public addFocusableElement(element: FocusableElement): void {
    this.focusableElements.set(element.id, element)

    // tabIndex を設定
    element.element.tabIndex = element.focusable ? element.tabIndex : -1

    // ARIA属性を設定
    if (element.description) {
      this.updateElementAttributes(element.element, element.description)
    }

    // フォーカスインジケータースタイルを追加
    if (this.config.showFocusIndicator) {
      element.element.style.outline = 'none'

      // キーボード操作のみでフォーカス表示（keyboardFocusOnlyがtrueの場合）
      if (this.config.keyboardFocusOnly) {
        element.element.addEventListener('focus', (_event: Event) => {
          if (!document.documentElement.classList.contains('mouse-navigation')) {
            element.element.style.outline = 'var(--a11y-focus-outline, 3px solid #007acc)'
          }
        })
      } else {
        element.element.addEventListener('focus', () => {
          element.element.style.outline = 'var(--a11y-focus-outline, 3px solid #007acc)'
        })
      }

      element.element.addEventListener('blur', () => {
        element.element.style.outline = 'none'
      })
    }
  }

  /**
   * フォーカス可能な要素をすべて取得
   */
  public getFocusableElements(): FocusableElement[] {
    return Array.from(this.focusableElements.values())
      .filter(item => item.focusable)
      .sort((a, b) => a.tabIndex - b.tabIndex)
  }

  /**
   * 特定の条件に一致するフォーカス可能な要素を検索
   */
  public findFocusableElement(predicate: (element: FocusableElement) => boolean): FocusableElement | undefined {
    return Array.from(this.focusableElements.values())
      .filter(item => item.focusable)
      .find(predicate)
  }

  /**
   * キーダウンハンドラー
   */
  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.config.keyboardNavigation) return

    // 最後のキーボードイベントを記録
    this.lastKeyEvent = event

    const pressedKeys = []
    if (event.ctrlKey) pressedKeys.push('Ctrl')
    if (event.shiftKey) pressedKeys.push('Shift')
    if (event.altKey) pressedKeys.push('Alt')
    if (event.metaKey) pressedKeys.push('Meta')

    // 特殊キーまたは通常キーを追加
    if (event.key !== 'Control' && event.key !== 'Shift' && event.key !== 'Alt' && event.key !== 'Meta') {
      pressedKeys.push(event.key)
    }

    // ショートカットをチェック
    const shortcutsArray = Array.from(this.shortcuts.entries());
    for (const [_id, shortcut] of shortcutsArray) {
      if (this.matchesKeySequence(pressedKeys, shortcut.keys)) {
        if (!shortcut.condition || shortcut.condition()) {
          event.preventDefault()
          shortcut.handler()
          this.announceAction(shortcut.description)
          break
        }
      }
    }
  }

  /**
   * キーシーケンスをマッチング
   */
  private matchesKeySequence(pressed: string[], target: string[]): boolean {
    if (pressed.length !== target.length) return false
    return target.every(key => pressed.includes(key))
  }

  /**
   * フォーカスハンドラー
   */
  private handleFocus(event: FocusEvent): void {
    const target = event.target as HTMLElement
    if (target && target.id) {
      this.currentFocus = target.id
      const description = this.descriptions.get(target.id)
      if (description && this.config.enableScreenReader) {
        this.announceElement(description)
      }

      // フォーカス内ループの処理
      if (this.config.focusLoop) {
        this.handleFocusLoop(target)
      }
    }
  }

  /**
   * フォーカス内ループ処理
   */
  private handleFocusLoop(target: HTMLElement): void {
    // フォーカス可能な要素のリスト
    const focusableElements = Array.from(this.focusableElements.values())
      .filter(item => item.focusable)
      .map(item => item.element)

    if (focusableElements.length === 0) return

    // 最初の要素でShift+Tabが押された場合、最後の要素にフォーカス
    if (target === focusableElements[0] &&
      (this.lastKeyEvent?.key === 'Tab' && this.lastKeyEvent?.shiftKey)) {
      focusableElements[focusableElements.length - 1].focus()
      if (event) event.preventDefault()
    }
    // 最後の要素でTabが押された場合、最初の要素にフォーカス
    else if (target === focusableElements[focusableElements.length - 1] &&
      (this.lastKeyEvent?.key === 'Tab' && !this.lastKeyEvent?.shiftKey)) {
      focusableElements[0].focus()
      if (event) event.preventDefault()
    }
  }

  // 最後のキーボードイベントを追跡
  private lastKeyEvent: KeyboardEvent | null = null

  /**
   * ブラーハンドラー
   */
  private handleBlur(_event: FocusEvent): void {
    this.currentFocus = null
  }

  /**
   * 次の要素にフォーカス
   */
  public focusNext(): void {
    const focusableIds = Array.from(this.focusableElements.keys())
      .filter(id => this.focusableElements.get(id)?.focusable)
      .sort((a, b) => {
        const aTab = this.focusableElements.get(a)?.tabIndex || 0
        const bTab = this.focusableElements.get(b)?.tabIndex || 0
        return aTab - bTab
      })

    if (focusableIds.length === 0) return

    const currentIndex = this.currentFocus ? focusableIds.indexOf(this.currentFocus) : -1
    const nextIndex = (currentIndex + 1) % focusableIds.length
    const nextId = focusableIds[nextIndex]

    this.focusElement(nextId)
  }

  /**
   * 前の要素にフォーカス
   */
  public focusPrevious(): void {
    const focusableIds = Array.from(this.focusableElements.keys())
      .filter(id => this.focusableElements.get(id)?.focusable)
      .sort((a, b) => {
        const aTab = this.focusableElements.get(a)?.tabIndex || 0
        const bTab = this.focusableElements.get(b)?.tabIndex || 0
        return aTab - bTab
      })

    if (focusableIds.length === 0) return

    const currentIndex = this.currentFocus ? focusableIds.indexOf(this.currentFocus) : 0
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableIds.length - 1
    const prevId = focusableIds[prevIndex]

    this.focusElement(prevId)
  }

  /**
   * メインコンテンツにスキップ
   */
  public skipToMainContent(): void {
    // 'main-content' IDを持つ要素にフォーカス
    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      mainContent.focus()
      this.currentFocus = 'main-content'
      this.announceAction('メインコンテンツにスキップしました')
    }
  }

  /**
   * ナビゲーションにスキップ
   */
  public skipToNavigation(): void {
    // 'navigation' IDを持つ要素にフォーカス
    const navigation = document.getElementById('navigation')
    if (navigation) {
      navigation.focus()
      this.currentFocus = 'navigation'
      this.announceAction('ナビゲーションにスキップしました')
    }
  }

  /**
   * マウスダウンハンドラー
   */
  private handleMouseDown(_event: MouseEvent): void {
    // マウス操作ではフォーカスインジケーターを非表示
    document.documentElement.classList.add('mouse-navigation')
  }

  /**
   * キーボードイベントハンドラー
   */
  private handleKeyboardEvent(_event: KeyboardEvent): void {
    // キーボード操作ではフォーカスインジケーターを表示
    document.documentElement.classList.remove('mouse-navigation')
  }

  /**
   * 指定要素にフォーカス
   */
  public focusElement(elementId: string): void {
    const focusable = this.focusableElements.get(elementId)
    if (focusable && focusable.focusable) {
      focusable.element.focus()
      this.currentFocus = elementId
    }
  }

  /**
   * 要素を音声で読み上げ
   */
  private announceElement(description: AccessibilityDescription): void {
    if (!this.config.enableScreenReader || !this.speechSynth) return

    let text = ''

    if (description.label) {
      text += description.label + '. '
    }

    if (description.elementType) {
      text += description.elementType + '. '
    }

    if (description.description) {
      text += description.description + '. '
    }

    if (description.value !== undefined) {
      text += `値: ${description.value}. `
    }

    if (description.state) {
      text += `状態: ${description.state}. `
    }

    this.speak(text)
  }

  /**
   * アクションを音声で通知
   */
  private announceAction(text: string): void {
    if (this.config.enableScreenReader) {
      this.speak(text)
    }
  }

  /**
   * テキストを音声で読み上げ
   */
  public speak(text: string): void {
    if (!this.speechSynth || !this.config.enableScreenReader) return

    // 既存の読み上げをキャンセル
    this.speechSynth.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    utterance.volume = this.config.audioVolume
    utterance.rate = 1.0
    utterance.pitch = 1.0

    this.speechSynth.speak(utterance)
  }

  /**
   * ライブリージョンにメッセージを追加
   */
  public announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.config.enableScreenReader) return

    // ライブリージョン要素を取得または作成
    let liveRegion = document.getElementById('a11y-live-region')
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'a11y-live-region'
      liveRegion.setAttribute('aria-live', priority)
      liveRegion.setAttribute('aria-atomic', 'true')
      liveRegion.style.position = 'absolute'
      liveRegion.style.left = '-10000px'
      liveRegion.style.top = 'auto'
      liveRegion.style.width = '1px'
      liveRegion.style.height = '1px'
      liveRegion.style.overflow = 'hidden'
      document.body.appendChild(liveRegion)
    }

    // メッセージを設定
    liveRegion.textContent = message

    // 音声読み上げも行う
    this.speak(message)
  }

  /**
   * ステータスメッセージを通知
   */
  public notifyStatus(message: string): void {
    this.announce(message, 'polite')
  }

  /**
   * 警告メッセージを通知
   */
  public notifyAlert(message: string): void {
    this.announce(message, 'assertive')
  }

  /**
   * 音声読み上げを切り替え
   */
  public toggleScreenReader(): void {
    this.config.enableScreenReader = !this.config.enableScreenReader
    this.notifyConfigChange('enableScreenReader', this.config.enableScreenReader)

    const message = this.config.enableScreenReader ? '音声読み上げが有効になりました' : '音声読み上げが無効になりました'
    this.speak(message)
  }

  /**
   * 高コントラストモードを切り替え
   */
  public toggleHighContrast(): void {
    this.config.highContrastMode = !this.config.highContrastMode
    this.updateCSSProperties()
    this.notifyConfigChange('highContrastMode', this.config.highContrastMode)

    this.announceAction(this.config.highContrastMode ? '高コントラストモードを有効にしました' : '高コントラストモードを無効にしました')
  }

  /**
   * 減速アニメーションを切り替え
   */
  public toggleReducedMotion(): void {
    this.config.reducedMotion = !this.config.reducedMotion
    this.updateCSSProperties()
    this.notifyConfigChange('reducedMotion', this.config.reducedMotion)

    this.announceAction(this.config.reducedMotion ? '減速アニメーションを有効にしました' : '減速アニメーションを無効にしました')
  }

  /**
   * 拡大率を変更
   */
  public changeMagnification(delta: number): void {
    const newMagnification = Math.max(0.5, Math.min(3.0, this.config.magnification + delta))
    this.setMagnification(newMagnification)
  }

  /**
   * 拡大率を設定
   */
  public setMagnification(scale: number): void {
    this.config.magnification = Math.max(0.5, Math.min(3.0, scale))
    this.updateCSSProperties()
    this.notifyConfigChange('magnification', this.config.magnification)

    this.announceAction(`拡大率を${Math.round(this.config.magnification * 100)}%に設定しました`)
  }

  /**
   * 色覚支援を設定
   */
  public setColorBlindSupport(type: AccessibilityConfig['colorBlindSupport']): void {
    this.config.colorBlindSupport = type
    this.notifyConfigChange('colorBlindSupport', type)

    const typeNames = {
      none: '標準',
      protanopia: '赤色盲',
      deuteranopia: '緑色盲',
      tritanopia: '青色盲'
    }

    this.announceAction(`${typeNames[type]}モードに切り替えました`)
  }

  /**
   * 設定を更新
   */
  public setConfig(config: Partial<AccessibilityConfig>): void {
    this.config = { ...this.config, ...config }
    this.updateCSSProperties()
    this.notifyConfigChange('config', this.config)
  }

  /**
   * 設定を取得
   */
  public getConfig(): AccessibilityConfig {
    return { ...this.config }
  }

  /**
   * 色コントラスト比を計算
   */
  public calculateContrastRatio(color1: string, color2: string): number {
    const luminance1 = this.getRelativeLuminance(color1)
    const luminance2 = this.getRelativeLuminance(color2)

    const lighter = Math.max(luminance1, luminance2)
    const darker = Math.min(luminance1, luminance2)

    return (lighter + 0.05) / (darker + 0.05)
  }

  /**
   * 相対輝度を計算
   */
  private getRelativeLuminance(color: string): number {
    // HEX形式をRGBに変換
    let r, g, b
    if (color.startsWith('#')) {
      const hex = color.replace('#', '')
      r = parseInt(hex.substring(0, 2), 16) / 255
      g = parseInt(hex.substring(2, 4), 16) / 255
      b = parseInt(hex.substring(4, 6), 16) / 255
    } else {
      // 他の色形式の処理（簡略化）
      return 0.5
    }

    // sRGBを線形RGBに変換
    r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
    g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
    b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

    // 相対輝度を計算
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  /**
   * 色コントラストがWCAG 2.1 AA基準を満たすか検証
   */
  public validateContrastRatio(color1: string, color2: string, fontSize: number = 16, isBold: boolean = false): boolean {
    const ratio = this.calculateContrastRatio(color1, color2)

    // フォントサイズと太さに基づく基準
    if (fontSize >= 18 || (fontSize >= 14 && isBold)) {
      // 大きな文字（AA基準: 3:1）
      return ratio >= 3.0
    } else {
      // 通常の文字（AA基準: 4.5:1）
      return ratio >= 4.5
    }
  }

  /**
   * コールバックを登録
   */
  public onConfigChange(callback: (config: AccessibilityConfig) => void): void {
    const id = `config-change-${Date.now()}`
    this.callbacks.set(id, callback)
  }

  /**
   * コールバックを削除
   */
  public offConfigChange(callback: (config: AccessibilityConfig) => void): void {
    for (const [id, cb] of this.callbacks) {
      if (cb === callback) {
        this.callbacks.delete(id)
        break
      }
    }
  }

  /**
   * 設定変更を通知
   */
  private notifyConfigChange(_key: string, _value: unknown): void {
    for (const callback of this.callbacks.values()) {
      callback(this.config)
    }
  }

  /**
   * 砍棄
   */
  public dispose(): void {
    // イベントリスナーを削除
    document.removeEventListener('keydown', this.handleKeyDown.bind(this))
    document.removeEventListener('focus', this.handleFocus.bind(this), true)
    document.removeEventListener('blur', this.handleBlur.bind(this), true)

    if (this.config.keyboardFocusOnly) {
      document.removeEventListener('mousedown', this.handleMouseDown.bind(this), true)
      document.removeEventListener('keydown', this.handleKeyboardEvent.bind(this), true)
    }

    // コールバックをクリア
    this.callbacks.clear()

    webglLogger.info('[AccessibilityManager] Disposed')
  }
}